import React from "react";
import {
  Box, IconButton, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography, alpha, Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import RefreshIcon from "@mui/icons-material/Refresh";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import { useGetPilotStatusQuery } from "../../../Api/attoDeskApi";
import { useAppSelector } from "../../../Store/hooks";
import { selectIsSuperAdmin } from "../../../Store/Auth/AuthSelector";
import PageLoader from "../../../Components/Loader/PageLoader";
import {
  NAVY, GOLD_SOFT, BORDER, TEXT_PRIMARY, TEXT_SECONDARY,
} from "../../../theme/tokens";

const PilotStatus: React.FC = () => {
  const navigate = useNavigate();
  const isSuper = useAppSelector(selectIsSuperAdmin);
  const { data, isLoading, refetch, isFetching } = useGetPilotStatusQuery(undefined, {
    skip: !isSuper, pollingInterval: 30_000,
  });

  if (!isSuper) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <Typography sx={{ fontSize: 14, color: TEXT_SECONDARY }}>
          Pilot status is visible to Super Admins only.
        </Typography>
      </Box>
    );
  }
  if (isLoading) return <PageLoader height="20vh" />;

  const s = ((data?.data as any) || {}) as {
    total: number; online: number; offline: number; disabled: number;
    avg_uptime_minutes: number;
    errors_last_24h: number; media_failures_24h: number; restart_count_24h: number;
    storage_warnings: Array<{ device_code: string; device_name: string; percent: number }>;
    top_problem_devices: Array<{ device_id: number; device_code: string; device_name: string; errors_24h: number }>;
    generated_at: string;
  };

  const fmtUptime = (mins: number) => {
    if (!mins) return "—";
    const h = Math.floor(mins / 60);
    if (h < 24) return `${h}h ${mins % 60}m`;
    return `${Math.floor(h / 24)}d ${h % 24}h`;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 1.5,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: `linear-gradient(135deg, ${alpha("#7c3aed", 0.18)} 0%, ${alpha("#7c3aed", 0.06)} 100%)`,
          color: "#7c3aed",
        }}>
          <RocketLaunchIcon />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography fontSize={20} fontWeight={800} color={NAVY} letterSpacing="-0.02em">
            Pilot Status
          </Typography>
          <Typography fontSize={12} color={TEXT_SECONDARY}>
            Live fleet snapshot · auto-refresh every 30s
            {isFetching ? " · refreshing…" : ""}
          </Typography>
        </Box>
        <IconButton onClick={() => refetch()}
          sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, width: 38, height: 38, bgcolor: "#fff" }}>
          <RefreshIcon sx={{ fontSize: 18, color: NAVY }} />
        </IconButton>
      </Box>

      {/* Top-line tiles */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2, mb: 3 }}>
        <Tile label="Online" value={s.online} color="#10b981" />
        <Tile label="Offline" value={s.offline} color="#ef4444" />
        <Tile label="Disabled" value={s.disabled} color="#94a3b8" />
        <Tile label="Total" value={s.total} color={NAVY} />
      </Box>

      {/* Stability tiles */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2, mb: 3 }}>
        <Tile label="Avg Uptime" value={fmtUptime(s.avg_uptime_minutes)} color={NAVY} />
        <Tile label="Errors (24h)" value={s.errors_last_24h}
          color={s.errors_last_24h > 0 ? "#dc2626" : "#10b981"} />
        <Tile label="Media Failures (24h)" value={s.media_failures_24h}
          color={s.media_failures_24h > 0 ? "#f59e0b" : "#10b981"} />
        <Tile label="Restarts (24h)" value={s.restart_count_24h}
          color={s.restart_count_24h > 5 ? "#dc2626" : NAVY} />
      </Box>

      {/* Storage warnings */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${BORDER}`, bgcolor: "#fff", mb: 3 }}>
        <Box sx={{ p: 2, borderBottom: `1px solid ${BORDER}` }}>
          <Typography sx={{ fontSize: 14, fontWeight: 800, color: NAVY }}>
            Storage warnings (≥85% used)
          </Typography>
        </Box>
        {(s.storage_warnings || []).length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center", color: TEXT_SECONDARY, fontSize: 13 }}>
            No devices over 85% storage. ✓
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Code", "Name", "Used %"].map(h => (
                    <TableCell key={h} sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY,
                      textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {s.storage_warnings.map((r) => (
                  <TableRow key={r.device_code}>
                    <TableCell sx={{ fontFamily: "monospace", fontWeight: 700 }}>{r.device_code}</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{r.device_name}</TableCell>
                    <TableCell>
                      <Chip size="small" label={`${r.percent}%`}
                        sx={{ height: 20, fontSize: 11, fontWeight: 700,
                          bgcolor: r.percent >= 95 ? "#fee2e2" : "#fef3c7",
                          color: r.percent >= 95 ? "#991b1b" : "#92400e" }} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Top problem devices */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${BORDER}`, bgcolor: "#fff" }}>
        <Box sx={{ p: 2, borderBottom: `1px solid ${BORDER}` }}>
          <Typography sx={{ fontSize: 14, fontWeight: 800, color: NAVY }}>
            Top problem devices (24h)
          </Typography>
          <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY }}>
            Sorted by error event count
          </Typography>
        </Box>
        {(s.top_problem_devices || []).length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center", color: TEXT_SECONDARY, fontSize: 13 }}>
            No problem devices reported. ✓
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Code", "Name", "Errors", ""].map(h => (
                    <TableCell key={h} sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY,
                      textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {s.top_problem_devices.map((r) => (
                  <TableRow key={r.device_id} hover sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/devices/${r.device_id}/monitoring`)}>
                    <TableCell sx={{ fontFamily: "monospace", fontWeight: 700 }}>{r.device_code}</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{r.device_name}</TableCell>
                    <TableCell>
                      <Chip size="small" label={r.errors_24h}
                        sx={{ height: 20, fontSize: 11, fontWeight: 700,
                          bgcolor: "#fee2e2", color: "#991b1b" }} />
                    </TableCell>
                    <TableCell align="right" sx={{ color: TEXT_SECONDARY, fontSize: 11 }}>
                      Open →
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {s.generated_at && (
        <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mt: 2, textAlign: "right" }}>
          Generated {new Date(s.generated_at).toLocaleString()}
        </Typography>
      )}
    </Box>
  );
};

const Tile: React.FC<{ label: string; value: React.ReactNode; color: string }> = ({ label, value, color }) => (
  <Paper elevation={0} sx={{
    p: 2.5, borderRadius: 3, border: `1px solid ${BORDER}`, bgcolor: "#fff",
  }}>
    <Typography sx={{
      fontSize: 10.5, fontWeight: 700, color: TEXT_SECONDARY,
      textTransform: "uppercase", letterSpacing: "0.06em",
    }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 30, fontWeight: 800, color, mt: 0.5, lineHeight: 1.1 }}>
      {value}
    </Typography>
  </Paper>
);

export default PilotStatus;
